import { useEffect, useState } from "react";
import { Sprout, BookOpen, ExternalLink, ListChecks } from "lucide-react";
import { fetchPrograms } from "@/lib/api";
import { ProgramCard } from "@/components/ProgramCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { NewsletterSection } from "@/components/NewsletterSection";

const GLOSSARY = [
  ["EPC (Earnings Per Click)", "Average revenue earned for every click sent to an offer. Calculated as total commissions ÷ total clicks. An EPC of $2.50 means each click is worth $2.50 on average."],
  ["AOV (Average Order Value)", "The average amount customers spend per order. Higher AOV usually means bigger commissions per sale, especially with percentage-based programs."],
  ["Cookie Duration", "How long after a click you still get credit for a sale. A 90-day cookie means if a user buys within 90 days of clicking your link, you earn the commission."],
  ["Recurring Commission", "You earn a commission every billing cycle (usually monthly) for as long as the customer stays subscribed — the key to passive affiliate income."],
  ["Payout Threshold", "The minimum balance you must earn before the program pays you out. Lower thresholds ($5-$25) mean you get paid faster."],
  ["Conversion Rate", "The percentage of clicks that turn into a sale or qualifying action. 2-3% is typical; well-matched audiences can hit 5%+."],
  ["CPA (Cost Per Action)", "A flat bounty paid for a specific action — a signup, trial or funded account — regardless of order value."],
  ["Two-Tier Program", "You also earn a cut when affiliates you recruit make sales (e.g., WP Engine pays $50 per sub-affiliate sale)."],
  ["Last-Click Attribution", "The most common model: the last affiliate link clicked before purchase gets 100% of the commission."],
];

const STEPS = [
  ["Pick one niche you understand", "Choose a niche where you can honestly evaluate products — your content quality (and conversions) depend on it."],
  ["Start with easy-approval programs", "Networks like Amazon Associates, ShareASale and ClickBank approve beginners instantly and let you test what converts."],
  ["Create comparison & tutorial content", "\"Best X for Y\" and \"How to set up Z\" content converts 3-5x better than general articles."],
  ["Prioritize recurring commissions", "One referral to a 30% recurring SaaS program can pay you every month for years."],
  ["Always disclose your links", "FTC rules require clear disclosure that you may earn commissions. It also builds reader trust."],
];

export default function BeginnersHubPage() {
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    fetchPrograms({ beginner_friendly: true, difficulty: "easy", sort: "popularity", limit: 6 })
      .then((d) => setPrograms(d.programs))
      .catch(console.error);
  }, []);

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <p className="uppercase text-xs tracking-[0.2em] font-semibold text-emerald-600 mb-2 flex items-center gap-1.5"><Sprout className="w-4 h-4" /> Beginner's Hub</p>
      <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-4">Start earning your first commission.</h1>
      <p className="text-slate-600 max-w-2xl mb-16 leading-relaxed">Curated low-barrier programs, a practical getting-started playbook, and every term you'll run into — explained in plain English.</p>

      <section className="mb-20">
        <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-8 flex items-center gap-2"><ListChecks className="w-6 h-6 text-emerald-600" /> Getting started in 5 steps</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {STEPS.map(([title, desc], i) => (
            <div key={title} className="bg-white border border-slate-200 rounded-lg p-6">
              <p className="font-heading text-3xl font-light text-emerald-600 mb-3">0{i + 1}</p>
              <p className="font-medium text-sm mb-2">{title}</p>
              <p className="text-xs text-slate-600 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-20">
        <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-8">Easiest programs to get approved for</h2>
        <div data-testid="beginner-programs-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {programs.map((p) => <ProgramCard key={p.slug} program={p} />)}
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-8">
        <div>
          <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-8 flex items-center gap-2"><BookOpen className="w-6 h-6 text-emerald-600" /> Glossary</h2>
          <Accordion type="single" collapsible data-testid="glossary-accordion" className="bg-white border border-slate-200 rounded-lg px-6">
            {GLOSSARY.map(([term, def]) => (
              <AccordionItem key={term} value={term}>
                <AccordionTrigger className="text-sm font-medium text-left">{term}</AccordionTrigger>
                <AccordionContent className="text-sm text-slate-600 leading-relaxed">{def}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
        <div>
          <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-8">Compliance resources</h2>
          <div className="space-y-4">
            {[
              ["FTC Endorsement Guides", "Official US rules on disclosing affiliate relationships in your content.", "https://www.ftc.gov/business-guidance/resources/ftcs-endorsement-guides-what-people-are-asking"],
              ["FTC Disclosures 101 for Influencers", "Plain-language guide on when and how to disclose.", "https://www.ftc.gov/business-guidance/resources/disclosures-101-social-media-influencers"],
              ["Amazon Associates Operating Agreement", "Required disclosure language if you promote Amazon products.", "https://affiliate-program.amazon.com/help/operating/agreement"],
              ["GDPR Overview", "If you have EU readers, understand cookie-consent requirements for affiliate tracking.", "https://gdpr.eu/what-is-gdpr/"],
            ].map(([title, desc, url]) => (
              <a key={title} href={url} target="_blank" rel="noopener noreferrer" className="card-lift flex items-start justify-between gap-4 bg-white border border-slate-200 rounded-lg p-6 group">
                <div>
                  <p className="font-medium text-sm group-hover:text-emerald-700 transition-colors duration-200">{title}</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">{desc}</p>
                </div>
                <ExternalLink className="w-4 h-4 text-slate-400 shrink-0" />
              </a>
            ))}
          </div>
        </div>
      </section>

      <NewsletterSection />
    </main>
  );
}
